import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, defaultRouteFor } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import WebsiteCreation from "@/pages/WebsiteCreation";
import MyWebsites from "@/pages/MyWebsites";
import SiteDetail from "@/pages/SiteDetail";
import CategoryProfiles from "@/pages/CategoryProfiles";
import ProfileEditor from "@/pages/ProfileEditor";
import Settings from "@/pages/Settings";
import ActivityReports from "@/pages/ActivityReports";
import Team from "@/pages/Team";
import ImageGenerator from "@/pages/ImageGenerator";
import DocumentEditor from "@/pages/DocumentEditor";
import StreetViewCleaner from "@/pages/StreetViewCleaner";
import NationalForm from "@/pages/NationalForm";
import NationalDetail from "@/pages/NationalDetail";
import SubpageForm from "@/pages/SubpageForm";

function Protected({ children, admin, module }) {
  const { user, ready, isAdmin, hasModule } = useAuth();
  const location = useLocation();
  if (!ready || user === null)
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (admin && !isAdmin) return <Navigate to={defaultRouteFor(user)} replace />;
  if (module && !hasModule(module)) return <Navigate to={defaultRouteFor(user)} replace />;
  return <Layout>{children}</Layout>;
}

function HomeRedirect() {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={defaultRouteFor(user)} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/create" element={<Protected module="website_generator"><WebsiteCreation /></Protected>} />
      <Route path="/create/national" element={<Protected module="website_generator"><NationalForm /></Protected>} />
      <Route path="/national/:id" element={<Protected module="website_generator"><NationalDetail /></Protected>} />
      <Route path="/national/:id/edit" element={<Protected module="website_generator"><NationalForm /></Protected>} />
      <Route path="/national/:id/subpages/new" element={<Protected module="website_generator"><SubpageForm /></Protected>} />
      <Route path="/national/:id/subpages/:sid" element={<Protected module="website_generator"><SubpageForm /></Protected>} />
      <Route path="/websites" element={<Protected module="website_generator"><MyWebsites /></Protected>} />
      <Route path="/websites/:id" element={<Protected module="website_generator"><SiteDetail /></Protected>} />
      <Route path="/images" element={<Protected module="image_generator"><ImageGenerator /></Protected>} />
      <Route path="/streetview" element={<Protected module="image_generator"><StreetViewCleaner /></Protected>} />
      <Route path="/documents" element={<Protected module="document_editor"><DocumentEditor /></Protected>} />
      <Route path="/profiles" element={<Protected admin><CategoryProfiles /></Protected>} />
      <Route path="/profiles/:id" element={<Protected admin><ProfileEditor /></Protected>} />
      <Route path="/activity" element={<Protected admin><ActivityReports /></Protected>} />
      <Route path="/team" element={<Protected admin><Team /></Protected>} />
      <Route path="/settings" element={<Protected admin><Settings /></Protected>} />
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster theme="dark" position="top-right" richColors />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
